import { useQuery } from '@tanstack/react-query';
import { supabaseQuery } from '../../../lib/supabaseQuery';
import { queryKeys } from '../../../lib/queryKeys';

/**
 * Shape of a conversation row as returned by the list query.
 *
 * `chat_messages` is fetched as an embedded resource over the
 * chat_messages.conversation_id FK, ordered created_at DESC and limited to
 * one row. Supabase embedded resources return an ARRAY, so the first element
 * is the latest message. This powers the preview line AND the unread badge
 * derivation (spec §3.2/§4.4) — one round-trip, no N+1.
 *
 * NOTE: the embedded ordering is applied via
 * `.order('created_at', { referencedTable: 'chat_messages' })`. A plain
 * `.order()` sorts only the PARENT rows, which is why the list used to show
 * an arbitrary message instead of the newest one.
 */
export interface ConversationMember {
    user_id: string;
    role: string;
    last_read_at: string | null;
    /** Per-conversation mute (phase-2 P3, migration 20260915150000). */
    muted?: boolean;
}

export interface Conversation {
    id: string;
    tenant_id: string;
    type: 'dm' | 'group';
    title: string | null;
    created_by: string;
    created_at: string;
    updated_at: string;
    chat_conversation_members: ConversationMember[];
    chat_messages: Array<{
        id: string;
        sender_id: string;
        body: string;
        created_at: string;
        deleted_at: string | null;
        data: Record<string, any>;
    }>;
}

export interface UseConversationsParams {
    tenantId: string | null | undefined;
    myUserId: string | null | undefined;
    enabled?: boolean;
}

/**
 * useConversations — the conversation list (left pane) for the standalone
 * messenger.
 *
 * NOTE: the embedded members select MUST include `muted` — the mute bell in
 * ChatView derives its state from `chat_conversation_members[my].muted`.
 * Dropping it here silently breaks the bell (always shows unmuted) while the
 * toggle itself keeps writing to the DB (owner-reported bug 2026-09-17).
 *
 * - Fetches my conversations with my member row (RLS already scopes to
 *   conversations I belong to; the .eq on my user id keeps the embedded
 *   members payload small).
 * - Embeds the latest message per conversation via the Supabase nested-select
 *   (spec §3.2 recommendation: one round-trip, RLS-filtered on both tables).
 * - Unread count per conversation derives from last_read_at vs the latest
 *   message — NOT the notifications table (spec §4.4 decision).
 * - All fetches carry .limit() per AGENTS.md §1.
 */
export function useConversations({ tenantId, myUserId, enabled = true }: UseConversationsParams) {
    const enabledFinal = Boolean(enabled && tenantId && myUserId);

    const query = useQuery<Conversation[]>({
        queryKey: queryKeys.messenger.conversations(tenantId || ''),
        queryFn: supabaseQuery<Conversation[]>(db =>
            db.from('chat_conversations')
                .select(`
                    id, tenant_id, type, title, created_by, created_at, updated_at,
                    chat_conversation_members (
                        user_id, role, last_read_at, muted
                    ),
                    chat_messages (
                        id, sender_id, body, created_at, deleted_at, data
                    )
                `)
                .eq('tenant_id', tenantId || '')
                .order('updated_at', { ascending: false })
                // The embedded chat_messages resource needs its OWN ordering:
                // a plain .order() sorts only the parent (conversation) rows.
                // Without this, PostgREST returned the messages in arbitrary
                // order, so the preview line and the unread badge read
                // whichever message came back first — not the newest.
                .order('created_at', { referencedTable: 'chat_messages', ascending: false })
                // Only the latest message is consumed (chat_messages[0]), so
                // fetch exactly one per conversation instead of the whole
                // history for all 50 conversations.
                .limit(1, { referencedTable: 'chat_messages' })
                .limit(50)
        ),
        enabled: enabledFinal,
        staleTime: 30_000,
    });

    /**
     * Unread count for one conversation: latest message is unread when it was
     * created after my last_read_at AND it is not mine. Zero when no messages
     * yet or no unread. Used by the sidebar badge (useChatUnread).
     */
    const unreadCountFor = (conversation: Conversation): number => {
        const mine = conversation.chat_conversation_members?.find(m => m.user_id === myUserId);
        if (!mine) return 0;
        const latest = conversation.chat_messages?.[0];
        if (!latest || latest.sender_id === myUserId) return 0;
        if (!latest.created_at) return 0;
        if (!mine.last_read_at) return 1;
        return new Date(latest.created_at) > new Date(mine.last_read_at) ? 1 : 0;
    };

    const totalUnread = (query.data || []).reduce((sum, c) => sum + unreadCountFor(c), 0);

    return {
        conversations: query.data || [],
        isLoading: query.isLoading,
        error: query.error,
        unreadCountFor,
        totalUnread,
    };
}

export default useConversations;

